import { getAccessToken } from './supabaseAuth';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

function hasConfig() {
  return Boolean(SUPABASE_URL && SUPABASE_KEY);
}

async function apiGet(path) {
  const token = getAccessToken() || SUPABASE_KEY;
  const response = await fetch(`${SUPABASE_URL}${path}`, {
    cache: 'no-store',
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${token}`
    }
  });

  const text = await response.text();
  if (!response.ok) {
    throw new Error(`Supabase request failed: ${response.status} ${text}`);
  }
  return text ? JSON.parse(text) : [];
}

function normalizeOptions(value, language) {
  const raw = Array.isArray(value) ? value : [];
  return raw
    .map((opt) => ({
      id: String(opt?.id || ''),
      text: language === 'ro' ? String(opt?.text_ro || '') : String(opt?.text_en || '')
    }))
    .filter((x) => x.id && x.text);
}

function shuffle(array) {
  const out = [...array];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i];
    out[i] = out[j];
    out[j] = tmp;
  }
  return out;
}

function letterIdByIndex(index) {
  if (index === 0) return 'a';
  if (index === 1) return 'b';
  if (index === 2) return 'c';
  return 'd';
}

function shuffleQuestionOptions(question) {
  const options = Array.isArray(question?.options) ? question.options : [];
  if (options.length < 2) return question;

  const originalCorrectId = String(question.correctOptionId || '');
  const indexed = options.map((opt, idx) => ({
    ...opt,
    _originalId: String(opt?.id || letterIdByIndex(idx))
  }));
  const shuffled = shuffle(indexed);

  let newCorrectId = '';
  const remapped = shuffled.map((opt, idx) => {
    const newId = letterIdByIndex(idx);
    if (opt._originalId === originalCorrectId) {
      newCorrectId = newId;
    }
    return {
      id: newId,
      text: String(opt?.text || '')
    };
  });

  // Safety fallback if source was malformed.
  if (!newCorrectId) {
    newCorrectId = remapped[0]?.id || 'a';
  }

  return {
    ...question,
    options: remapped,
    correctOptionId: newCorrectId
  };
}

export function isQuizHubConfigured() {
  return hasConfig();
}

function normalizeDifficulty(value) {
  const v = String(value || '').toLowerCase();
  if (v === 'expert') return 'expert';
  if (v === 'hard') return 'hard';
  if (v === 'medium') return 'medium';
  return 'easy';
}

export async function fetchQuizHubBank(language = 'ro') {
  if (!hasConfig()) {
    return {
      questions: [],
      difficultyCounts: { easy: 0, medium: 0, hard: 0, expert: 0 },
      totalQuestions: 0
    };
  }

  const rows = await apiGet(
    `/rest/v1/quiz_question_bank?select=id,topic_slug,topic_label_ro,topic_label_en,source_lesson_slug,difficulty,question_ro,question_en,options,correct_option_id,explanation_ro,explanation_en,is_active,created_at&is_active=eq.true&or=(source_lesson_slug.is.null,source_lesson_slug.eq.)&order=topic_slug.asc,created_at.asc`
  );

  const filtered = rows || [];
  const questions = [];
  const difficultyCounts = { easy: 0, medium: 0, hard: 0, expert: 0 };

  for (let i = 0; i < filtered.length; i += 1) {
    const row = filtered[i];
    const difficulty = normalizeDifficulty(row?.difficulty);

    const normalizedQuestion = {
      id: String(row.id || ''),
      lessonSlug: '',
      lessonTitle: language === 'ro' ? String(row.topic_label_ro || '') : String(row.topic_label_en || ''),
      topicSlug: String(row.topic_slug || ''),
      question: language === 'ro' ? String(row.question_ro || '') : String(row.question_en || ''),
      options: normalizeOptions(row.options, language),
      correctOptionId: String(row.correct_option_id || ''),
      explanation: language === 'ro' ? String(row.explanation_ro || '') : String(row.explanation_en || ''),
      difficulty
    };

    if (!normalizedQuestion.id || !normalizedQuestion.question || normalizedQuestion.options.length < 2) continue;
    questions.push(normalizedQuestion);
    difficultyCounts[difficulty] += 1;
  }

  return {
    questions,
    difficultyCounts,
    totalQuestions: questions.length
  };
}

export function buildQuizSession(questions, difficulty = 'easy', maxQuestions = 10) {
  const list = Array.isArray(questions) ? questions : [];
  const normDifficulty = normalizeDifficulty(difficulty);
  const filtered = list.filter((q) => normalizeDifficulty(q?.difficulty) === normDifficulty);
  const capped = Math.max(1, Math.min(20, Number(maxQuestions || 10)));
  const picked = shuffle(filtered).slice(0, Math.min(capped, filtered.length));
  return picked.map((q) => shuffleQuestionOptions(q));
}
